import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

import Button from '@/components/Button';
import Card from '@/components/Card';
import Page from '@/components/Page';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import useAuth from '@/hooks/useAuth';
import {
  experimentsAPI,
  extractExperimentsApiError,
  ExperimentDetail,
  ExperimentSummary,
  FeatureFlagState,
  FeatureFlagSummary,
} from '@/services/experimentsAPI';

import CreateFlagModal from './CreateFlagModal';
import ExperimentModal from './ExperimentModal';
import ExperimentRow from './ExperimentRow';

const STATE_COLORS: Record<
  FeatureFlagState,
  'default' | 'success' | 'warning' | 'info'
> = {
  on: 'success',
  off: 'default',
  experimenting: 'info',
  paused: 'warning',
};

type ExperimentModalState = {
  flagKey: string;
  experiment: ExperimentDetail | null;
};

const FeatureFlags = () => {
  // Route access is already gated by PrivateRoute (requiredRoles:
  // [TENANT_ADMIN], see routes/index.tsx), matching ActionsLog/
  // ClientSessions. This is a defensive second gate so the screen never
  // renders for a customer admin even if reached another way.
  const { currentAdminIsCustomerAdmin } = useAuth();

  const [flags, setFlags] = useState<FeatureFlagSummary[] | null>(null);
  const [experimentsByFlag, setExperimentsByFlag] = useState<
    Record<string, ExperimentSummary[]>
  >({});
  // Per-flag error, so one flag's experiments failing to load (e.g. a 500)
  // doesn't blank out every other flag's experiment table.
  const [experimentErrorsByFlag, setExperimentErrorsByFlag] = useState<
    Record<string, string>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [togglingKey, setTogglingKey] = useState<string | null>(null);
  const [showCreateFlag, setShowCreateFlag] = useState(false);
  const [experimentModal, setExperimentModal] =
    useState<ExperimentModalState | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const flagList = await experimentsAPI.listFlags();
      setFlags(flagList);

      // allSettled, not all: one flag's experiments failing to load must not
      // reject the whole batch and blank every other flag's table.
      const results = await Promise.allSettled(
        flagList.map((flag) => experimentsAPI.listExperiments(flag.key)),
      );
      const nextExperiments: Record<string, ExperimentSummary[]> = {};
      const nextErrors: Record<string, string> = {};
      results.forEach((result, i) => {
        const flagKey = flagList[i].key;
        if (result.status === 'fulfilled') {
          nextExperiments[flagKey] = result.value;
        } else {
          nextErrors[flagKey] = extractExperimentsApiError(
            result.reason,
            'Failed to load experiments',
          );
        }
      });
      setExperimentsByFlag(nextExperiments);
      setExperimentErrorsByFlag(nextErrors);
    } catch (err: any) {
      setError(extractExperimentsApiError(err, 'Failed to load feature flags'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip the fetch entirely for a customer admin — this screen redirects
    // them below, and firing the request anyway would just be wasted work
    // (and risk a setState-after-redirect warning).
    if (currentAdminIsCustomerAdmin) return;
    load();
  }, [load, currentAdminIsCustomerAdmin]);

  if (currentAdminIsCustomerAdmin) {
    return <Navigate to="/restricted-access" replace />;
  }

  // Kill-switch toggle. Optimistic: graphy busts its own lookup cache on
  // commit, so the only thing standing between the click and reality is this
  // request — on failure we put the switch back and say why, rather than
  // leaving the UI asserting a state the server never accepted.
  const toggleDefaultEnabled = async (flag: FeatureFlagSummary) => {
    const next = !flag.default_enabled;
    setTogglingKey(flag.key);
    setError(null);
    setFlags((prev) =>
      (prev ?? []).map((f) =>
        f.key === flag.key ? { ...f, default_enabled: next } : f,
      ),
    );
    try {
      const updated = await experimentsAPI.updateFlag(flag.key, {
        default_enabled: next,
      });
      // Take the server's row: `state` is derived server-side, so the chip
      // beside the switch would otherwise keep the pre-toggle value.
      setFlags((prev) =>
        (prev ?? []).map((f) =>
          f.key === flag.key ? { ...f, ...updated } : f,
        ),
      );
    } catch (err: any) {
      setFlags((prev) =>
        (prev ?? []).map((f) =>
          f.key === flag.key
            ? { ...f, default_enabled: flag.default_enabled }
            : f,
        ),
      );
      setError(
        extractExperimentsApiError(
          err,
          `Failed to ${next ? 'enable' : 'disable'} ${flag.key}`,
        ),
      );
    } finally {
      setTogglingKey(null);
    }
  };

  const openNewExperiment = (flagKey: string) =>
    setExperimentModal({ flagKey, experiment: null });

  const openEditExperiment = async (flagKey: string, experimentId: number) => {
    try {
      const detail = await experimentsAPI.getExperiment(flagKey, experimentId);
      setExperimentModal({ flagKey, experiment: detail });
    } catch (err: any) {
      setError(extractExperimentsApiError(err, 'Failed to load experiment'));
    }
  };

  return (
    <Page size="full" title="Feature Flags & Experiments">
      <Stack vertical gap={24} align="start" style={{ width: '100%' }}>
        <Stack align="center" justify="apart" style={{ width: '100%' }}>
          <Small color="lighter">
            Graphy&apos;s native A/B feature-flag engine — internal ops only.
          </Small>
          <Button
            a11yLabel="New flag"
            label="New flag"
            onClick={() => setShowCreateFlag(true)}
          />
        </Stack>

        {error && (
          <div style={{ width: '100%' }}>
            <Alert severity="error">{error}</Alert>
          </div>
        )}

        {loading && flags === null && (
          <Stack justify="center" style={{ width: '100%', padding: 40 }}>
            <CircularProgress />
          </Stack>
        )}

        {flags && flags.length === 0 && !loading && (
          <Body color="lighter">
            No feature flags yet. Create one to get started.
          </Body>
        )}

        {flags?.map((flag) => (
          <Card key={flag.key}>
            <Card.Section>
              <Stack align="center" justify="apart">
                <Stack vertical gap={4} align="start">
                  <Stack gap={8} align="center">
                    <Body weight="bold">{flag.name}</Body>
                    <Chip
                      label={flag.state}
                      size="small"
                      color={STATE_COLORS[flag.state] ?? 'default'}
                    />
                  </Stack>
                  <Small color="lighter">{flag.key}</Small>
                  {flag.description && (
                    <Small color="lighter">{flag.description}</Small>
                  )}
                </Stack>
                <Stack gap={8} align="center" style={{ width: 'auto' }}>
                  {/* Only meaningful without an active experiment: graphy
                      resolves an experimenting flag per user and ignores the
                      default, so offering the switch there would imply control
                      it doesn't have. Kill-switch flags have no experiment. */}
                  {!flag.active_experiment && (
                    <FormControlLabel
                      label={flag.default_enabled ? 'Enabled' : 'Disabled'}
                      control={
                        <Switch
                          size="small"
                          checked={flag.default_enabled}
                          disabled={togglingKey === flag.key}
                          inputProps={{
                            'aria-label': `Toggle ${flag.key}`,
                          }}
                          onChange={() => toggleDefaultEnabled(flag)}
                        />
                      }
                    />
                  )}
                  <Button
                    a11yLabel={`New experiment for ${flag.key}`}
                    label="New experiment"
                    appearance="plain"
                    onClick={() => openNewExperiment(flag.key)}
                  />
                </Stack>
              </Stack>
            </Card.Section>
            <Card.Section>
              {experimentErrorsByFlag[flag.key] ? (
                <Alert severity="error">
                  {experimentErrorsByFlag[flag.key]}
                </Alert>
              ) : (experimentsByFlag[flag.key] ?? []).length === 0 ? (
                <Small color="lighter">No experiments yet.</Small>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Key</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Rollout %</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(experimentsByFlag[flag.key] ?? []).map((exp) => (
                        <ExperimentRow
                          key={exp.id}
                          flagKey={flag.key}
                          experiment={exp}
                          onEdit={() => openEditExperiment(flag.key, exp.id)}
                          onChanged={load}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card.Section>
          </Card>
        ))}
      </Stack>

      {showCreateFlag && (
        <CreateFlagModal
          onClose={() => setShowCreateFlag(false)}
          onCreated={() => {
            setShowCreateFlag(false);
            load();
          }}
        />
      )}

      {experimentModal && (
        <ExperimentModal
          flagKey={experimentModal.flagKey}
          experiment={experimentModal.experiment}
          onClose={() => setExperimentModal(null)}
          onSaved={() => {
            setExperimentModal(null);
            load();
          }}
        />
      )}
    </Page>
  );
};

export default FeatureFlags;
